import { useState } from 'react';

interface MapDashboardProps {
  selectedMetroRegion: string;
  selectedBasicRegions: string[];
  selectedCategory: string;
  viewMode: 'map' | 'table';
  setSelectedMetroRegion: (region: string) => void;
  setSelectedBasicRegions: (regions: string[]) => void;
}

export default function MapDashboard({ 
  selectedMetroRegion, 
  selectedBasicRegions, 
  selectedCategory, 
  viewMode,
  setSelectedMetroRegion,
  setSelectedBasicRegions
}: MapDashboardProps) {
  const [sortOrder, setSortOrder] = useState<'high' | 'low'>('high');

  const metroRegionData = [
    { name: '서울특별시', grade: 'A', score: 92, change: '+2', color: 'bg-green-500' },
    { name: '부산광역시', grade: 'B', score: 85, change: '+1', color: 'bg-blue-500' },
    { name: '대구광역시', grade: 'B', score: 83, change: '0', color: 'bg-blue-500' },
    { name: '인천광역시', grade: 'A', score: 89, change: '+3', color: 'bg-green-500' },
    { name: '광주광역시', grade: 'C', score: 76, change: '-1', color: 'bg-yellow-500' },
    { name: '대전광역시', grade: 'B', score: 81, change: '+1', color: 'bg-blue-500' },
    { name: '울산광역시', grade: 'C', score: 78, change: '0', color: 'bg-yellow-500' },
    { name: '세종특별자치시', grade: 'A', score: 94, change: '+4', color: 'bg-green-500' },
    { name: '경기도', grade: 'A', score: 88, change: '+2', color: 'bg-green-500' },
    { name: '강원도', grade: 'C', score: 74, change: '-2', color: 'bg-yellow-500' },
    { name: '충청북도', grade: 'B', score: 80, change: '+1', color: 'bg-blue-500' },
    { name: '충청남도', grade: 'B', score: 82, change: '+2', color: 'bg-blue-500' },
    { name: '전라북도', grade: 'C', score: 75, change: '-1', color: 'bg-yellow-500' },
    { name: '전라남도', grade: 'C', score: 73, change: '-2', color: 'bg-yellow-500' },
    { name: '경상북도', grade: 'B', score: 79, change: '0', color: 'bg-blue-500' },
    { name: '경상남도', grade: 'B', score: 84, change: '+1', color: 'bg-blue-500' },
    { name: '제주특별자치도', grade: 'A', score: 87, change: '+3', color: 'bg-green-500' }
  ];

  const basicRegionData: Record<string, any[]> = {
    '서울특별시': [
      { name: '강남구', grade: 'A', score: 95, change: '+3', color: 'bg-green-500' },
      { name: '서초구', grade: 'A', score: 93, change: '+2', color: 'bg-green-500' },
      { name: '송파구', grade: 'A', score: 91, change: '+1', color: 'bg-green-500' },
      { name: '중구', grade: 'B', score: 88, change: '0', color: 'bg-blue-500' },
      { name: '종로구', grade: 'B', score: 86, change: '+1', color: 'bg-blue-500' },
      { name: '용산구', grade: 'B', score: 84, change: '-1', color: 'bg-blue-500' },
      { name: '마포구', grade: 'B', score: 82, change: '+2', color: 'bg-blue-500' },
      { name: '영등포구', grade: 'C', score: 78, change: '0', color: 'bg-yellow-500' },
      { name: '구로구', grade: 'C', score: 76, change: '-1', color: 'bg-yellow-500' },
      { name: '금천구', grade: 'C', score: 74, change: '-2', color: 'bg-yellow-500' }
    ],
    '경기도': [
      { name: '성남시', grade: 'A', score: 90, change: '+2', color: 'bg-green-500' },
      { name: '수원시', grade: 'A', score: 89, change: '+1', color: 'bg-green-500' },
      { name: '고양시', grade: 'B', score: 85, change: '+1', color: 'bg-blue-500' },
      { name: '용인시', grade: 'B', score: 83, change: '0', color: 'bg-blue-500' },
      { name: '부천시', grade: 'B', score: 81, change: '+1', color: 'bg-blue-500' },
      { name: '안산시', grade: 'C', score: 77, change: '-1', color: 'bg-yellow-500' },
      { name: '안양시', grade: 'C', score: 75, change: '0', color: 'bg-yellow-500' },
      { name: '남양주시', grade: 'C', score: 73, change: '-2', color: 'bg-yellow-500' }
    ]
  };

  // 현재 표시할 데이터 결정
  const getCurrentData = () => {
    if (selectedMetroRegion === '전국') {
      return metroRegionData;
    } else if (selectedBasicRegions.length > 0) {
      const allBasicData = basicRegionData[selectedMetroRegion] || [];
      return allBasicData.filter(region => selectedBasicRegions.includes(region.name));
    } else {
      return basicRegionData[selectedMetroRegion] || [];
    }
  };

  const currentData = getCurrentData();
  const sortedData = [...currentData].sort((a, b) => {
    return sortOrder === 'high' ? b.score - a.score : a.score - b.score;
  });

  // 지도 이미지 URL 결정
  const getMapImageUrl = () => {
    if (selectedMetroRegion === '전국') {
      return "https://public.readdy.ai/ai/img_res/777fe401c283c7e61e7ddc95e0173d6c.jpg";
    } else {
      return `https://readdy.ai/api/search-image?query=$%7BselectedMetroRegion%7D%20detailed%20administrative%20map%20showing%20district%20divisions%20with%20color-coded%20regulatory%20environment%20zones%2C%20clean%20government%20dashboard%20style%2C%20professional%20data%20visualization%20with%20clear%20boundaries&width=600&height=400&seq=${selectedMetroRegion}-map-1&orientation=landscape`;
    }
  };

  // 지역 클릭 핸들러
  const handleRegionClick = (regionName: string) => {
    if (selectedMetroRegion === '전국') {
      // 전국 지도에서 광역자치단체 클릭
      setSelectedMetroRegion(regionName);
    } else {
      // 광역자치단체 지도에서 기초자치단체 클릭
      const basicRegionsByMetro: Record<string, string[]> = {
        '서울특별시': ['강남구', '서초구', '송파구', '중구', '종로구', '용산구', '마포구', '영등포구', '구로구', '금천구'],
        '경기도': ['성남시', '수원시', '고양시', '용인시', '부천시', '안산시', '안양시', '남양주시']
      };
      
      const availableRegions = basicRegionsByMetro[selectedMetroRegion] || [];
      if (availableRegions.includes(regionName)) {
        if (selectedBasicRegions.includes(regionName)) {
          setSelectedBasicRegions(selectedBasicRegions.filter(r => r !== regionName));
        } else {
          setSelectedBasicRegions([...selectedBasicRegions, regionName]);
        }
      }
    }
  };

  if (viewMode === 'map') {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-6 h-full flex flex-col">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3 sm:gap-0">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
            <i className="ri-map-2-line text-blue-600 mr-2"></i>
            <span className="truncate">{selectedMetroRegion === '전국' ? '전국 규제환경 지도' : `${selectedMetroRegion} 규제환경 지도`}</span>
          </h3>
          
          <div className="flex items-center space-x-2 sm:space-x-3 text-xs sm:text-sm w-full sm:w-auto">
            <span className="text-gray-600 whitespace-nowrap">정렬:</span>
            <button
              onClick={() => setSortOrder('high')}
              className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                sortOrder === 'high' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              높은순위
            </button>
            <button
              onClick={() => setSortOrder('low')}
              className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                sortOrder === 'low' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              낮은순위
            </button>
          </div>
        </div>

        {/* 지도 영역 */}
        <div className="relative bg-gray-50 rounded-lg p-4 sm:p-8 mb-4 sm:mb-6 cursor-pointer flex-1 min-h-[300px] sm:min-h-0">
          <div className="absolute inset-0 flex items-center justify-center">
            <img 
              src={getMapImageUrl()}
              alt={`${selectedMetroRegion} 규제환경 지도`}
              className="w-full h-full object-contain rounded-lg"
              onClick={() => {
                // 지도 클릭 시 샘플 지역 선택 (실제로는 클릭 좌표에 따라 지역 결정)
                if (selectedMetroRegion === '전국') {
                  handleRegionClick('서울특별시');
                } else {
                  const sampleRegions = basicRegionData[selectedMetroRegion] || [];
                  if (sampleRegions.length > 0) {
                    handleRegionClick(sampleRegions[0].name);
                  }
                }
              }}
            />
          </div>
          
          {/* 클릭 안내 */}
          <div className="absolute top-2 sm:top-4 left-2 sm:left-4 bg-white p-2 rounded-lg shadow-sm border">
            <p className="text-xs text-gray-600">
              {selectedMetroRegion === '전국' 
                ? '지역을 클릭하여 광역자치단체를 선택하세요' 
                : '지역을 클릭하여 기초자치단체를 선택하세요'}
            </p>
          </div>
          
          {/* 지도 범례 */}
          <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4 bg-white p-2 sm:p-3 rounded-lg shadow-sm border">
            <h4 className="text-xs sm:text-sm font-medium text-gray-900 mb-2">규제환경 등급</h4>
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 sm:w-4 sm:h-4 bg-green-500 rounded"></div>
                <span className="text-xs text-gray-600">A등급 (우수)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 sm:w-4 sm:h-4 bg-blue-500 rounded"></div>
                <span className="text-xs text-gray-600">B등급 (양호)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 sm:w-4 sm:h-4 bg-yellow-500 rounded"></div>
                <span className="text-xs text-gray-600">C등급 (보통)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 sm:w-4 sm:h-4 bg-red-500 rounded"></div>
                <span className="text-xs text-gray-600">D등급 (개선필요)</span>
              </div>
            </div>
          </div>

          {/* 선택된 지역 정보 */}
          {selectedBasicRegions.length > 0 && (
            <div className="absolute top-2 sm:top-4 right-2 sm:right-4 bg-white p-2 sm:p-3 rounded-lg shadow-sm border max-w-[200px] sm:max-w-xs">
              <h4 className="text-xs sm:text-sm font-medium text-gray-900 mb-2">선택된 기초자치단체</h4>
              <div className="flex flex-wrap gap-1">
                {selectedBasicRegions.map(region => (
                  <span key={region} className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                    {region}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 요약 통계 */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
          <div className="bg-green-50 p-3 sm:p-4 rounded-lg">
            <div className="text-xl sm:text-2xl font-bold text-green-700">
              {currentData.filter(d => d.grade === 'A').length}
            </div>
            <div className="text-xs sm:text-sm text-green-600">A등급 지역</div>
          </div>
          <div className="bg-blue-50 p-3 sm:p-4 rounded-lg">
            <div className="text-xl sm:text-2xl font-bold text-blue-700">
              {currentData.filter(d => d.grade === 'B').length}
            </div>
            <div className="text-xs sm:text-sm text-blue-600">B등급 지역</div>
          </div>
          <div className="bg-yellow-50 p-3 sm:p-4 rounded-lg">
            <div className="text-xl sm:text-2xl font-bold text-yellow-700">
              {currentData.filter(d => d.grade === 'C').length}
            </div>
            <div className="text-xs sm:text-sm text-yellow-600">C등급 지역</div>
          </div>
          <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
            <div className="text-xl sm:text-2xl font-bold text-gray-700">
              {currentData.length > 0 ? (currentData.reduce((sum, d) => sum + d.score, 0) / currentData.length).toFixed(1) : '0'}
            </div>
            <div className="text-xs sm:text-sm text-gray-600">평균 점수</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-6 h-full flex flex-col">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3 sm:gap-0">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
          <i className="ri-table-line text-blue-600 mr-2"></i>
          <span className="truncate">{selectedMetroRegion === '전국' ? '전국 규제환경 순위표' : `${selectedMetroRegion} 규제환경 순위표`}</span>
        </h3>
        
        <div className="flex items-center space-x-2 sm:space-x-3 w-full sm:w-auto">
          <button
            onClick={() => setSortOrder('high')}
            className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
              sortOrder === 'high' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            높은순위
          </button>
          <button
            onClick={() => setSortOrder('low')}
            className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
              sortOrder === 'low' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            낮은순위
          </button>
        </div>
      </div>

      <div className="overflow-auto flex-1 -mx-3 sm:mx-0">
        <table className="w-full min-w-[600px]">
          <thead className="sticky top-0 bg-white">
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs">순위</th>
              <th className="text-left py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs">지역명</th>
              <th className="text-left py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs">등급</th>
              <th className="text-left py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs">점수</th>
              <th className="text-left py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs">변화</th>
              <th className="text-left py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs">액션</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((region, index) => (
              <tr key={region.name} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <td className="py-3 px-2 sm:px-4 text-xs font-medium text-gray-900">{index + 1}</td>
                <td className="py-3 px-2 sm:px-4 text-xs text-gray-900">{region.name}</td>
                <td className="py-3 px-2 sm:px-4">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    region.grade === 'A' ? 'bg-green-100 text-green-800' :
                    region.grade === 'B' ? 'bg-blue-100 text-blue-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`} style={{ fontSize: '10px' }}>
                    {region.grade}등급
                  </span>
                </td>
                <td className="py-3 px-2 sm:px-4 text-xs text-gray-900 font-medium">{region.score}</td>
                <td className="py-3 px-2 sm:px-4">
                  <span className={`inline-flex items-center font-medium ${
                    region.change.startsWith('+') ? 'text-green-600' :
                    region.change.startsWith('-') ? 'text-red-600' :
                    'text-gray-600'
                  }`} style={{ fontSize: '10px' }}>
                    {region.change.startsWith('+') && <i className="ri-arrow-up-line mr-1"></i>}
                    {region.change.startsWith('-') && <i className="ri-arrow-down-line mr-1"></i>}
                    {region.change === '0' && <i className="ri-subtract-line mr-1"></i>}
                    {region.change}
                  </span>
                </td>
                <td className="py-3 px-2 sm:px-4">
                  <div className="flex items-center space-x-2">
                    <button className="p-1 text-gray-400 hover:text-yellow-500 transition-colors">
                      <i className="ri-star-line text-xs"></i>
                    </button>
                    <button className="p-1 text-gray-400 hover:text-blue-500 transition-colors">
                      <i className="ri-eye-line text-xs"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
